import React, { useState } from 'react';
import { useRouter } from '@hooks/useRouter';
import styled from '@emotion/styled';
import { usePleaseLogIn } from '@hooks/usePleaseLogIn';
import useIsRestricted from '@hooks/useIsRestricted';
import { toggleContrast } from '@components/Accessibility/AccessibilityContainer';
import { useTranslation } from 'react-i18next';
import { Button } from 'antd';
import {
  HomeFilled,
  CalendarFilled,
  QuestionCircleFilled,
  InfoCircleFilled,
  CarryOutFilled,
  BgColorsOutlined,
  ContainerFilled,
  PlusCircleFilled,
  MailOutlined
} from 'antd/icons';
import type { AntdIconProps } from '@ant-design/icons/lib/components/AntdIcon';
import Link from '@components/utils/Link';
import { InfoModal } from 'vl-common/src/components/Modals';
import UIVersionLabel from 'vl-common/src/components/UIVersionLabel';
import { useQuery } from '@tanstack/react-query';
import * as actions from '@actions';
import { useDispatch } from 'react-redux';
import useApi from 'vl-common/src/hooks/useApi';
import { HarFunctionsType } from 'vl-common/src/hooks/useHARFunctions';
import { MoreMenu } from './MoreMenu';
import ExercisePlan from '@components/Patients/ExercisePlan/ExercisePlan';
import useAuth from '@vl-core/useAuth';
import useRuntimeConfig from '@vl-core/useRuntimeConfig';

const StyledFooter = styled.footer`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-around;

  gap: 0.5em;
  z-index: 1000;
  background-color: var(--productPrimary);
  box-shadow: 0 -6px 6px rgba(0, 0, 0, 0.12);
  padding: 0 1em;

  width: 100%;
  position: relative;

  @media (max-width: 532px) {
    padding: 0 0.5em;
  }
`;

const StyledAboutDiv = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex-wrap: wrap;

  .footer-content {
    display: flex;
    flex-direction: row;
    justify-content: space-around;
    align-items: center;
    margin-bottom: 12px;
  }

  .dev-tools {
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: center;
    gap: 8px;

    .dev-tools-button {
      height: 60px;
    }
  }

  .har-buttons {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;

    .har-button {
      width: 48%;
      height: 60px;
    }
  }
`;

const StyledButton = styled.button<{ active: boolean; badge: string | null }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: ${({ active }) => (active ? '#ffffff' : 'transparent')};
  color: #000000;
  width: 20%;
  border: 0;
  padding: 18px 0 14px 0;
  cursor: pointer;

  .nav-icon {
    font-size: 24px;
    color: ${({ active }) => (active ? 'var(--active-nav-item-color)' : 'var(--primary-btn-font-color)')};
    margin-bottom: 4px;
  }

  ${({ badge }) =>
    badge !== null
      ? `.nav-icon::after {
        content: '${badge}';
        border-radius: 16px;
        background-color: red;
        display: inline-block;
        height: 15px;
        font-size: 12px;
        position: absolute;
        padding: 0 6px;
        top: 10px;
        margin-left: 16px;
        line-height: 16px;
        color: #ffffff;
        font-weight: bold;
      }`
      : ''}
  .nav-text {
    color: ${({ active }) => (active ? 'var(--active-nav-item-color)' : 'var(--primary-btn-font-color)')};
  }

  @media (max-width: 672px) {
    font-size: 12px;
  }
  @media (max-width: 532px) {
    font-size: 10px;
  }

  ${({ active }) =>
    !active &&
    `
    body[data-contrast='high'] & {
      .nav-icon {
        color: #fff;
      }

      .nav-text {
        color: #fff;
      }
    }
  `}
`;

type MenuButtonProps = {
  name: string;
  Icon: React.ComponentType<AntdIconProps>;
  action: () => void;
  testid: string;
  active: boolean;
  badge?: string | null;
  restricted?: boolean;
};

const MenuButton = ({ name, Icon, action, testid, active, badge = null, restricted = false }: MenuButtonProps) => {
  const isRestricted = useIsRestricted();
  const [, setPleaseLogIn] = usePleaseLogIn();

  const onClickHandler = restricted && isRestricted ? () => setPleaseLogIn(true) : action;

  return (
    <StyledButton data-testid={testid} onClick={onClickHandler} active={active} badge={badge}>
      <Icon aria-hidden className="nav-icon" />
      <span className="nav-text">{name}</span>
    </StyledButton>
  );
};

type NavigationFooterProps = {
  logRocketSessionURL: string;
  isProd: boolean;
  theme: any;
  cycleThemes: () => void;
  DATABASE_VERSION: string;
  harProps: HarFunctionsType;
};

export const NavigationFooter = ({
  logRocketSessionURL,
  isProd,
  theme,
  cycleThemes,
  DATABASE_VERSION,
  harProps
}: NavigationFooterProps) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { pathname } = router;
  const { t } = useTranslation();
  const { Api } = useApi();
  const { user } = useAuth();
  const isRestricted = useIsRestricted();
  const { CONTACT_US_URL } = useRuntimeConfig();

  const [aboutModalOpen, setAboutModalOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);

  const { clearButtonProps, recordButtonProps, dumpButtonProps, testingButtonProps } = harProps;

  const isPatient = user?.user_type_code === 'PATIENT';

  const physitrackLink = user?.physitrack_link;
  const showExercisePlanButton = isPatient && !isRestricted && !!physitrackLink;

  const { data: allCases = [] } = useQuery({
    queryKey: ['fetchAllCases'],
    queryFn: () =>
      Api.cases.getCases({
        order: 'desc',
        rows: 999,
        sort: 'todo'
      }),
    select: ({ status, cases }) => (status === 204 ? [] : cases || [])
  });
  const noCases = allCases.length === 0;

  const { data: todo = [] } = useQuery({
    queryKey: ['getNextSteps'],
    queryFn: () => dispatch(actions.getIncompletedToDosList()),
    select: (data) => data.todo
  });

  const todoCount = todo.length;
  const noTodos = todo.length === 0;
  const todoBadgeText = todoCount >= 10 ? '9+' : todoCount.toString();
  // If at least one case & at least one to-do, then show badge on todos page
  const todoBadgeContent = noCases || noTodos ? null : todoBadgeText;
  // If no cases but there are todos, then show badge on home page
  //      They will be patient level, so inaccessible on todos page
  const homeBadgeContent = !noTodos && noCases ? todoBadgeText : null;

  const menuItems = [
    {
      name: 'Home',
      action: () => router.push('/patients'),
      active: pathname === '/patients',
      testid: 'patients-dashboard-link',
      Icon: HomeFilled,
      badge: homeBadgeContent
    },
    {
      name: t('Next steps'),
      action: () => router.push('/patients/todos'),
      active: pathname === '/patients/todos',
      testid: 'patients-todos-link',
      restricted: true,
      Icon: CarryOutFilled,
      badge: todoBadgeContent
    },
    {
      name: 'Appointments',
      action: () => router.push('/patients/appointments'),
      active:
        pathname === '/patients/appointments' ||
        pathname.includes('/patients/timeline') ||
        pathname.includes('/patients/session') ||
        pathname.includes('/patients/choose'),
      restricted: true,
      testid: 'patients-appointments-link',
      Icon: CalendarFilled
    },
    {
      name: 'Documents',
      action: () => router.push('/patients/documents'),
      active: pathname === '/patients/documents',
      testid: 'patients-documents-link',
      restricted: true,
      Icon: ContainerFilled
    },
    {
      name: 'More',
      action: () => setMoreMenuOpen(!moreMenuOpen),
      active: moreMenuOpen || pathname === '/patients/support' || aboutModalOpen,
      testid: 'patients-more-link',
      Icon: PlusCircleFilled
    }
  ];

  const moreMenuItems = [
    {
      name: 'Contact Us',
      action: () => window.open(CONTACT_US_URL, '_blank'),
      active: false,
      testid: 'contact-us-link',
      Icon: MailOutlined
    },
    {
      name: 'Support',
      action: () => router.push('/patients/support'),
      active: pathname === '/patients/support',
      testid: 'patients-support-link',
      Icon: QuestionCircleFilled
    },
    {
      name: 'About',
      action: () => {
        setAboutModalOpen(true);
      },
      testid: 'patients-about-link',
      active: aboutModalOpen,
      Icon: InfoCircleFilled
    },
    {
      name: 'Contrast',
      action: () => {
        toggleContrast();
      },
      active: false,
      testid: 'patients-contrast-link',
      Icon: BgColorsOutlined
    }
  ];

  return (
    <StyledFooter>
      {showExercisePlanButton && (
        <div
          style={{
            position: 'absolute',
            bottom: '100%',
            left: '16px',
            marginBottom: '0px',
            zIndex: 10
          }}
        >
          <ExercisePlan url={physitrackLink} />
        </div>
      )}
      {menuItems.map((item) => (
        <MenuButton key={item.name} {...item} data-testid={item.testid} />
      ))}
      <MoreMenu open={moreMenuOpen} options={moreMenuItems} onClose={() => setMoreMenuOpen(false)} />

      <InfoModal
        title="About"
        open={aboutModalOpen}
        onCancel={() => setAboutModalOpen(false)}
        okButtonProps={{ style: { display: 'none' } }}
        cancelText="Close"
      >
        <StyledAboutDiv>
          <div className="footer-content">
            <UIVersionLabel />
          </div>

          <div className="footer-content">
            <a
              rel="noreferrer"
              target="_blank"
              href="/patients/legal/?all_policies=0&activeTab=USAGE"
              tabIndex={0}
              data-testid="terms"
            >
              {t('Terms')}
            </a>
            <Link href="/accessibility" data-testid="accessibility">
              {t('Accessibility')}
            </Link>
            <a href="/regulatory">
              <img src="/static/images/ukca.png" alt={t('UKCA Mark (UK Conformity Assessed)')} width="25px" />
            </a>
            {logRocketSessionURL && (
              <a href={logRocketSessionURL}>
                <img src="/static/images/logRocket.png" width="30px" alt={t('LogRocket')} />
              </a>
            )}
          </div>

          {!isProd && (
            <div className="dev-tools">
              <span>{t('Dev Tools:')}</span>
              <Button data-testid="dev-change-theme" onClick={cycleThemes} className="dev-tools-button">
                {t('Theme(')}
                {theme.id})
              </Button>
              <div data-testid="db-version">
                {t('DB Version: ')}
                {DATABASE_VERSION}
              </div>
              <div className="har-buttons">
                <Button
                  data-testid="clear-har-button"
                  className="har-button"
                  onClick={clearButtonProps.props?.onClick}
                  disabled={clearButtonProps.props?.disabled}
                >
                  {clearButtonProps.text}
                </Button>
                <Button {...recordButtonProps.props} className="har-button" data-testid="record-button">
                  {recordButtonProps.text}
                </Button>
                <Button
                  data-testid="dump-har-button"
                  onClick={(e) => {
                    dumpButtonProps.props?.onClick?.(e);
                    setAboutModalOpen(false);
                  }}
                  className="har-button"
                >
                  {dumpButtonProps.text}
                </Button>
                <Button
                  {...testingButtonProps.props}
                  onClick={(e) => {
                    testingButtonProps.props?.onClick?.(e);
                    setAboutModalOpen(false);
                  }}
                  data-testid="testing-mode-button"
                  className="har-button"
                >
                  {testingButtonProps.text}
                </Button>
              </div>
            </div>
          )}
        </StyledAboutDiv>
      </InfoModal>
    </StyledFooter>
  );
};
